// The comparator is SHARED (FNP-912/923) — nulls-last in both directions,
// numeric vs string, and empty-string-counts-as-missing live in one place.
// Do not write another one here.
import {useTableSort} from '@flexinet/ui-components';

import {useTestDefinitionsQuery} from './useTestDefinitionsQuery';
import {useTestDefinitionsSearch} from './useTestDefinitionsSearch';
import {useTestDefinitionsTableColumns} from './useTestDefinitionsTableColumns';
// Only the DEFAULT stays local: WHICH key this table sorts by is a per-entity
// judgement, unlike HOW two values compare.
import {DEFAULT_SORT} from '../utils/sortTestDefinitions';
import {useApplicationNameResolver} from '../../testing-api-integration/hooks/useApplicationOptions';

export const useTestDefinitions = (filters: Record<string, string> = {}) => {
    const {
        data: items = [],
        isLoading: loading,
        isError,
    } = useTestDefinitionsQuery(filters);
    const {keyword} = useTestDefinitionsSearch();
    const {columns} = useTestDefinitionsTableColumns();
    const applicationNameFor = useApplicationNameResolver();

    // Client-side keyword filtering. `keyword` comes from the module-scoped store
    // in useTestDefinitionsSearchQuery, so the value typed into the table toolbar
    // in a sibling component actually reaches this filter (FNP-815).
    const needle = keyword.trim()
                          .toLowerCase();

    const visibleItems = items.filter(item =>
        !needle ||
        item.testCode?.toLowerCase()
            .includes(needle) ||
        item.name?.toLowerCase()
            .includes(needle) ||
        // Matches the application NAME shown in the column, not the raw
        // `applicationId` — the id is an opaque uuid the operator never sees, so
        // matching on it alone would make the column look unsearchable. The id
        // still matches as a fallback for a dangling reference, which is
        // exactly the case where the id IS what is on screen.
        applicationNameFor(item.applicationId)?.toLowerCase()
            .includes(needle) ||
        item.applicationId?.toLowerCase()
            .includes(needle) ||
        item.status?.toLowerCase()
            .includes(needle)
    );

    // Sorted AFTER filtering so the ordering describes what is on screen.
    // Sort state is component-level by design (FNP-912): it is how ONE operator
    // is viewing this table, unlike the keyword, which is module-scoped because
    // a sibling toolbar sets it.
    const {
        sortStatus,
        setSortStatus,
        sorted: records,
    } = useTableSort(visibleItems, DEFAULT_SORT);

    return {
        items,
        visibleItems,
        records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        columns,
    };
};
